import { useContext, useEffect, useRef, useState } from "react";
import { Badge } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import "react-calendar/dist/Calendar.css";
import { useMediaQuery } from "react-responsive";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { v4 as uuidv4 } from "uuid";
import { Store } from "./Store";
import AdminRoute from "./components/AdminRoute";
import CompartmentList from "./components/CompartmentList";
import ConnectedClient from "./components/ConnectedClient";
import HelpDeskProtectedRoute from "./components/HelpDeskProtectedRoute";
import MobileMenu from "./components/MobileMenu";
import OrderFilter from "./components/OrderFilter";
import ProtectedRoute from "./components/ProtectedRoute";
import WebMenu from "./components/WebMenu";
import { onMessageListener, requestForToken } from "./firebase";
import useCompartments from "./hooks/useCompartments";
import useLiveOrders from "./hooks/useLiveOrders";
import useProductsAndCategories from "./hooks/useProductsAndCategories";
import useTableTypes from "./hooks/useTableTypes";
import useTableUsers from "./hooks/useTableUsers";
import useUsers from "./hooks/useUsers";
import AccountProfileScreen from "./screens/AccountProfileScreen";
import BusinessUserStatsScreen from "./screens/BusinessUserStatsScreen";
import CategoricOrderScreen from "./screens/CategoricOrderScreen";
import CategoricOrderScreenMobile from "./screens/CategoricOrderScreenMobile";
import CategoryListScreen from "./screens/CategoryListScreen";
import CategorySampleListScreen from "./screens/CategorySampleListScreen";
import ChatScreen from "./screens/ChatScreen";
import CustomerChangePassword from "./screens/CustomerChangePassword";
import CustomerSignIn from "./screens/CustomerSignIn";
import CustomerSignup from "./screens/CustomerSignup";
import DailyReportScreen from "./screens/DailyReportScreen";
import DashboardScreen from "./screens/DashboardScreen";
import ExpenseListScreen from "./screens/ExpenseListScreen";
import HelpDeskScreen from "./screens/HelpDeskScreen";
import ManagementScreen from "./screens/ManagementScreen";
import MembershipListScreen from "./screens/MembershipListScreen";
import OrderTouchScreen from "./screens/OrderTouchScreen";
import PeriodicReportScreen from "./screens/PeriodicReportScreen";
import PrintDivById from "./screens/PrintDivById";
import PrintReceipt from "./screens/PrintReceipt";
import ProductListScreen from "./screens/ProductListScreen";
import ProductSampleListScreen from "./screens/ProductSampleListScreen";
import ProductSeed from "./screens/ProductSeed";
import ProductStockOutputScreen from "./screens/ProductStockOutputScreen";
import QrMenu from "./screens/QrMenu";
import SigninNewPassword from "./screens/SigninNewPassword";
import SigninScreen from "./screens/SigninScreen";
import SignupScreen from "./screens/SignupScreen";
import StateMachine from "./screens/StateMachine";
import StockInputListScreen from "./screens/StockInputListScreen";
import SummaryScreen from "./screens/SummaryScreen";
import TableTypeListScreen from "./screens/TableTypeListScreen";
import TableTypeSampleList from "./screens/TableTypeSampleListScreen";
import TableTypeSeed from "./screens/TableTypeSeed";
import UserListScreen from "./screens/UserListScreen";

function App() {
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { carts, userInfo, messages } = state;
  const [notDeliveredItemExist, setNotDeliveredItemExist] = useState(false);
  const [toggle, setToggle] = useState(false);
  const { pathname } = useLocation();
  const [chatScreenVisable, setChatScreenVisable] = useState(false);

  const navigate = useNavigate();

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const smallComputer = useMediaQuery({ query: "(max-width: 1700px)" });

  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectInterval = 5000; // Reconnect every 5 seconds if disconnected

  const [, , setLoadTableTypes] = useTableTypes();
  const [, , setLoadTableUsers] = useTableUsers();
  const [, , setLoadUsers] = useUsers();
  const [, , setLoadCompartments] = useCompartments();
  const [, , setLoadOrders] = useLiveOrders();
  const [, , setLoadProducts, setLoadCategories] = useProductsAndCategories();

  const signoutHandler = () => {
    ctxDispatch({ type: "USER_SIGNOUT" });
    navigate("/signin");
  };

  useEffect(() => {
    requestForToken();

    onMessageListener().then((payload) => {
      console.log("Notification Received: ", payload);
      toast.success(payload.notification.title);
    });
  }, []);

  useEffect(() => {
    const setOrder = (order) => {
      const orders = [{ ...order }].map((tableOrder) => {
        const items = tableOrder.orderItems.map((item) => ({
          ...item.product,
          menuName: item.name,
          price: item.price || item.product.price,
          quantity: item.quantity,
          tableNum: tableOrder.tableNum,
          createdAt: item.createdAt,
          itemCode: item.itemCode,
          relativeItem: item.relativeItem,
          mainPrice: item.mainPrice,
          tableCode: tableOrder.tableCode,
          feature: item.feature,
          clientCode: item.clientCode || tableOrder.clientCode,
          customerName: item.customerName || tableOrder.description,
          address: item.address,
          answers: item.answers,
          alert: item.alert,
          isPartialPaid: item.isPartialPaid,
        }));
        return {
          description: {
            tableNum: tableOrder.tableNum,
            clientCode: tableOrder.clientCode,
            user: tableOrder.user,
            tableCode: tableOrder.tableCode,
            customerName: tableOrder.description,
            alert: tableOrder.alert,
            orderCode: tableOrder.orderCode || uuidv4(),
            alertTimePeriod: tableOrder.alertTimePeriod,
            onBusinessUser: tableOrder.onBusinessUser,
            tableType: tableOrder.tableType,
            alertOn: tableOrder.alertOn ?? true,
            partialCache: tableOrder.partialCache,
            partialPOS: tableOrder.partialPOS,
          },
          items,
        };
      });

      ctxDispatch({
        type: "CUSTOMER_SET_ORDERS",
        payload: { orders },
      });
    };

    const connectEventSource = () => {
      // Set up the SSE connection
      const domain = window.location.origin;
      //const domain = "http://localhost:5000";
      eventSourceRef.current = new EventSource(
        `${domain}/events?businessOwner=${
          !userInfo.client
            ? userInfo.membership.businessOwner
            : userInfo.client.businessOwner
        }`
      );

      eventSourceRef.current.onopen = () => {
        console.log("EventSource connected");
        setIsConnected(true);
        setLoadOrders(true);
      };

      // Listen for events
      eventSourceRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.objectName === "Order") {
            setOrder(message);
          } else if (message.objectName === "TableType") {
            setLoadTableTypes(true);
          } else if (message.objectName === "BusinessUser") {
            setLoadUsers(true);
          } else if (message.objectName === "Product") {
            setLoadProducts(true);
            setLoadCategories(true);
          } else if (message.objectName === "Category") {
            setLoadProducts(true);
            setLoadCategories(true);
          } else if (message.objectName === "Compartment") {
            setLoadCompartments(true);
          } else if (message.objectName === "TableUser") {
            setLoadTableUsers(true);
          }
        } catch (err) {}
      };

      // eventSourceRef.current.onerror = async () => {
      //   console.error("EventSource encountered an error");

      //   // Check if the issue is a connection problem
      //   if (eventSourceRef.current.readyState === EventSource.CONNECTING) {
      //     console.warn("EventSource is reconnecting...");
      //   } else if (eventSourceRef.current.readyState === EventSource.CLOSED) {
      //     console.warn("EventSource connection is closed");
      //   }

      //   // Use a fetch request to verify server accessibility and get error details
      //   try {
      //     const domain = window.location.origin;
      //     const response = await fetch(`${domain}/events`, {
      //       method: "GET",
      //     });
      //     if (!response.ok) {
      //       console.error("Fetch error:", response.status, response.statusText);
      //       if (response.status === 401) {
      //         console.warn("Unauthorized access - check authentication.");
      //       } else if (response.status === 403) {
      //         console.warn("Forbidden access - check permissions.");
      //       } else if (response.status >= 500) {
      //         console.warn("Server error - server might be down.");
      //       }
      //     } else {
      //       console.warn("Server is reachable, possible SSE misconfiguration.");
      //     }
      //   } catch (error) {
      //     console.error(
      //       "Fetch failed - likely a network issue or server is down:",
      //       error.message
      //     );
      //   }
      // };
    };

    if (userInfo && !userInfo.isCustomer) {
      connectEventSource();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [userInfo]);

  useEffect(() => {
    let cartsOrders = carts
      .filter((c) => c.alert)
      .filter(
        (cart) =>
          cart.cartItems.filter((item) => item.tableCode || item.clientCode)
            .length > 0
      );

    const cartItemNotDelivered = cartsOrders.filter(
      (cart) =>
        cart.cartItems.filter(
          (item) =>
            (item.tableCode || item.clientCode) && !item.delivered && item.alert
        ).length > 0
    );
    setNotDeliveredItemExist(cartItemNotDelivered.length > 0);
  }, [carts]);

  const toggleSidebar = (toggleToThis) => {
    setToggle(toggleToThis || !toggle);
  };

  const openSidebar = () => {
    setToggle(true);
  };
  const closeSidebar = () => {
    setToggle(false);
  };

  useEffect(() => {
    if (document.getElementById("connectedClientDragDiv")) {
      var pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      var elmnt = document.getElementById("connectedClientDragDiv");
      elmnt.addEventListener("mousedown", dragMouseDown);
      elmnt.addEventListener("touchstart", touchStart);

      function dragMouseDown(e) {
        e = e || window.event;
        e.preventDefault();
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e = e || window.event;
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      }

      function closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
      }

      function touchStart(e) {
        e = e || window.event;
        e.preventDefault();
        var touch = e.touches[0];
        pos3 = touch.clientX;
        pos4 = touch.clientY;
        document.addEventListener("touchend", closeDragElement2);
        document.addEventListener("touchmove", touchMove);
      }

      function touchMove(e) {
        e = e || window.event;
        e.preventDefault();
        var touch = e.touches[0];
        pos1 = pos3 - touch.clientX;
        pos2 = pos4 - touch.clientY;
        pos3 = touch.clientX;
        pos4 = touch.clientY;
        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      }

      function closeDragElement2() {
        document.removeEventListener("touchend", closeDragElement);
        document.removeEventListener("touchmove", touchMove);
      }
    }
  }, [userInfo]);

  return (
    <div>
      <Container
        className={
          smallComputer || isTabletOrMobile
            ? "d-flex site-fullwidth p-0 bg-dark"
            : "d-flex site-maxwidth p-0 bg-dark"
        }
      >
        {!isTabletOrMobile &&
          pathname !== "/signin" &&
          pathname !== "/newpassword" &&
          pathname !== "/signup" &&
          pathname !== "/qrmenu" &&
          pathname !== "/order/touch" && (
            <WebMenu
              toggle={toggle}
              pathname={pathname}
              userInfo={userInfo}
              toggleSidebar={toggleSidebar}
              openSidebar={openSidebar}
              closeSidebar={closeSidebar}
              isTabletOrMobile={isTabletOrMobile}
              notDeliveredItemExist={notDeliveredItemExist}
              signoutHandler={signoutHandler}
            />
          )}
        <div
          className={
            pathname === "/signin" ||
            pathname === "/signup" ||
            pathname === "/newpassword"
              ? "d-flex flex-column site-container bg-white signin-background"
              : "d-flex flex-column site-container bg-white"
          }
          style={{ width: "100%" }}
        >
          <ToastContainer
            position={isTabletOrMobile ? "bottom-center" : "top-center"}
            limit={1}
            autoClose={2000}
          />
          <header>
            {pathname !== "/cart" &&
              pathname !== "/qrmenu" &&
              isTabletOrMobile && (
                <MobileMenu
                  userInfo={userInfo}
                  isTabletOrMobile={isTabletOrMobile}
                  notDeliveredItemExist={notDeliveredItemExist}
                  signoutHandler={signoutHandler}
                />
              )}
          </header>

          <main
            className={
              pathname === "/category/order" ||
              pathname === "/cart" ||
              pathname === "/order/touch"
                ? "bg-dark "
                : ""
            }
          >
            <Container
              className={`${
                isTabletOrMobile
                  ? "upmedium-container-mobile"
                  : "upmedium-container"
              } mt-3 ${pathname === "/qrmenu" ? "p-0" : ""}`}
              style={{
                paddingLeft:
                  pathname === "/order/touch"
                    ? "40px"
                    : pathname === "/signin" ||
                      pathname === "/signup" ||
                      pathname === "/newpassword"
                    ? "40px"
                    : isTabletOrMobile
                    ? "var(--bs-gutter-x,.75rem)"
                    : toggle && pathname !== "/qrmenu"
                    ? "320px"
                    : !toggle && pathname !== "/qrmenu"
                    ? "120px"
                    : "0px",

                transition: "padding-left 0.5s",
              }}
            >
              <Routes>
                <Route path="/signin" element={<SigninScreen />} />
                <Route path="/newpassword" element={<SigninNewPassword />} />
                <Route path="/customer_signin" element={<CustomerSignIn />} />
                <Route path="/signup" element={<SignupScreen />} />
                <Route path="/customer_signup" element={<CustomerSignup />} />
                <Route
                  path="/customer_changepass"
                  element={<CustomerChangePassword />}
                />

                <Route
                  path="/category/order"
                  element={
                    isTabletOrMobile ? (
                      <ProtectedRoute>
                        <CategoricOrderScreenMobile />
                      </ProtectedRoute>
                    ) : (
                      <ProtectedRoute>
                        <CategoricOrderScreen />
                      </ProtectedRoute>
                    )
                  }
                />
                <Route
                  path="/order/touch"
                  element={
                    <ProtectedRoute>
                      <OrderTouchScreen />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AdminRoute>
                      <AccountProfileScreen />
                    </AdminRoute>
                  }
                />

                <Route
                  path="/dailyreport"
                  element={
                    <ProtectedRoute>
                      <DailyReportScreen />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/periodicreport"
                  element={
                    <ProtectedRoute>
                      <PeriodicReportScreen />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/reportsummary"
                  element={
                    <ProtectedRoute>
                      <SummaryScreen />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route path="/printdiv" element={<PrintDivById />}></Route>
                <Route path="/state-machine" element={<StateMachine />}></Route>

                {/* Admin Routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminRoute>
                      <DashboardScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/management"
                  element={
                    <AdminRoute>
                      <ManagementScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/productseed"
                  element={
                    <AdminRoute>
                      <ProductSeed />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/tabletypeseed"
                  element={
                    <AdminRoute>
                      <TableTypeSeed />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/orders-filter"
                  element={
                    <AdminRoute>
                      <OrderFilter />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <UserListScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/products"
                  element={
                    <AdminRoute>
                      <ProductListScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/tabletypes"
                  element={
                    <AdminRoute>
                      <TableTypeListScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/product-samples"
                  element={
                    <HelpDeskProtectedRoute>
                      <ProductSampleListScreen />
                    </HelpDeskProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/admin/tabletype-samples"
                  element={
                    <HelpDeskProtectedRoute>
                      <TableTypeSampleList />
                    </HelpDeskProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/admin/business-user-stats"
                  element={
                    <HelpDeskProtectedRoute>
                      <BusinessUserStatsScreen />
                    </HelpDeskProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/admin/business-list"
                  element={
                    <HelpDeskProtectedRoute>
                      <MembershipListScreen />
                    </HelpDeskProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/admin/category-samples"
                  element={
                    <HelpDeskProtectedRoute>
                      <CategorySampleListScreen />
                    </HelpDeskProtectedRoute>
                  }
                ></Route>
                <Route
                  path="/admin/categories"
                  element={
                    <AdminRoute>
                      <CategoryListScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/compartments"
                  element={
                    <AdminRoute>
                      <CompartmentList />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/stockInputs"
                  element={
                    <AdminRoute>
                      <StockInputListScreen />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="/admin/expenses"
                  element={
                    <AdminRoute>
                      <ExpenseListScreen tarihAsc={false} />
                    </AdminRoute>
                  }
                ></Route>

                <Route
                  path="/admin/print-adisyon"
                  element={
                    <AdminRoute>
                      <PrintReceipt />
                    </AdminRoute>
                  }
                ></Route>

                <Route
                  path="/admin/product/stockout/:id"
                  element={
                    <AdminRoute>
                      <ProductStockOutputScreen />
                    </AdminRoute>
                  }
                ></Route>

                <Route
                  path="/"
                  element={
                    (userInfo && userInfo.isCustomer) ||
                    (!userInfo && localStorage.getItem("successCode")) ? (
                      <Navigate
                        to={`/qrmenu?code=${
                          userInfo?.membership?.businessOwner ||
                          userInfo?.client?.businessOwner ||
                          localStorage.getItem("successCode")
                        }`}
                      ></Navigate>
                    ) : (
                      <Navigate to={"/category/order"}></Navigate>
                    )
                  }
                />
                <Route path="/qrmenu" element={<QrMenu />} />
                <Route
                  path="*"
                  element={<Navigate to={"/"} replace={true}></Navigate>}
                />
              </Routes>
              {pathname !== "/signin" &&
                pathname !== "/newpassword" &&
                pathname !== "/signup" &&
                pathname !== "/qrmenu" && (
                  <>
                    <div
                      style={{
                        position: "fixed",
                        bottom: "0px",
                        right: "0px",
                        marginRight: "10px",
                        marginBottom: "10px",
                        zIndex: 999,
                      }}
                    >
                      {
                        <div
                          style={
                            chatScreenVisable
                              ? { visibility: "visible" }
                              : { visibility: "hidden" }
                          }
                        >
                          {userInfo?.isHelpDesk ? (
                            <HelpDeskScreen
                              chatScreenVisable={chatScreenVisable}
                              setChatScreenVisable={setChatScreenVisable}
                            />
                          ) : (
                            <ChatScreen
                              chatScreenVisable={chatScreenVisable}
                              setChatScreenVisable={setChatScreenVisable}
                            />
                          )}
                        </div>
                      }
                      {!chatScreenVisable && (
                        <div style={{ position: "relative" }}>
                          <Link
                            to="#"
                            onClick={() => setChatScreenVisable(true)}
                          >
                            <span className="chat-icon-bubble">
                              <i className="fas fa-comment-dots" />
                            </span>
                          </Link>

                          {messages &&
                            messages.filter(
                              (m) => !m.read && m.user.id !== userInfo._id
                            ).length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "-2px",
                                  right: "2px",
                                  fontSize: "20px",
                                  fontWeight: "bold",
                                }}
                              >
                                {messages && messages.length > 0 && (
                                  <Badge pill bg="danger">
                                    {
                                      messages?.filter(
                                        (m) =>
                                          !m.read && m.user.id !== userInfo._id
                                      ).length
                                    }
                                  </Badge>
                                )}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                    {userInfo?.isHelpDesk && userInfo?.client && (
                      <div
                        id="connectedClientDragDiv"
                        style={{
                          cursor: "move",
                          position: "fixed",
                          top: "20px",
                          left: isTabletOrMobile
                            ? "20px"
                            : "calc(100vw - 200px)",
                        }}
                      >
                        <ConnectedClient userInfo={userInfo} />
                      </div>
                    )}
                  </>
                )}
            </Container>
          </main>
          <footer></footer>
        </div>
      </Container>
    </div>
  );
}

export default App;
